import { useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getEventDetails } from '@store/slices/eventSlice';
import { getImageSrc } from '@utils/fn';

interface DocImageGeneratorProps {
  templateSrc: string;
  docLabel: string;
  logoUrl?: string;
  className?: string;
}

const Frame = styled.div`
  --rot: -6.7deg;
  --logo-top: 16%;
  --logo-left: 8%;
  --logo-w: 42%;

  --ribbon-top: 33%;
  --ribbon-left: 7%;
  --ribbon-right: 10%;
  --ribbon-pad-v: 15%;
  --ribbon-pad-h: 6%;
  --ribbon-opacity: 0.9;

  --title-top: 40%;
  --title-side-pad: 5%;
  --title-lines: 3;

  --accent-right: 29%;
  --accent-bottom: 13.3%;
  --accent-w: 1.6%;
  --accent-h: 5.5%;

  position: relative;
  width: 100%;
  max-width: 44rem;
  aspect-ratio: 3 / 4;
  background: #fff;
  
  
`;

const Wrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 1.2rem;
  overflow: hidden;
  padding: clamp(6px, 2.2%, 20px) clamp(10px, 4.5%, 32px);
  box-sizing: border-box;
  transform: scale(var(--shrink, 0.87));
  transform-origin: center;

`;

const Bg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  z-index: 1;
`;

const Logo = styled.img`
  position: absolute;
  z-index: 3;
  top: var(--logo-top);
  left: var(--logo-left);
  width: var(--logo-w);
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.2));
  transform: rotate(var(--rot));
  transform-origin: left center;
`;

const Title = styled.div`
  position: absolute;
  z-index: 5;
  left: calc(var(--ribbon-left) + var(--title-side-pad));
  right: calc(var(--ribbon-right) + var(--title-side-pad));
  top: var(--title-top);
  transform: rotate(var(--rot));
  transform-origin: left center;

  color: #ffffff;
  font-weight: 400;
  line-height: 1.2;
  font-size: clamp(14px, 2.1vw, 18px);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--title-lines);
  overflow: hidden;
  word-break: break-word;
`;

const BottomRibbon = styled.div<{ $color: string }>`
  position: absolute;
  z-index: 4;
  left: var(--ribbon-left);
  right: var(--ribbon-right);
  top: var(--ribbon-top);

  background: ${({ $color }) => $color};
  opacity: var(--ribbon-opacity);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  transform: rotate(var(--rot));
  transform-origin: left center;

  padding: var(--ribbon-pad-v) var(--ribbon-pad-h)
    calc(var(--ribbon-pad-v) * 0.4) var(--ribbon-pad-h);

  color: #111;
  font-weight: 900;
  text-transform: uppercase;
  text-align: left;
  line-height: 1.12;
  letter-spacing: 0.02em;
  font-size: clamp(16px, 3vw, 26px);

  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
  width: 78%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: var(--ribbon-pad-h);
    right: var(--ribbon-pad-h);
    bottom: clamp(6px, 1.2vw, 10px);
    height: 2px;
    background: rgba(0, 0, 0, 0.22);
    border-radius: 2px;
  }
`;

const RibbonTopCap = styled.div`
  position: absolute;
  z-index: 5;
  top: calc(var(--ribbon-top) - 0.2%);
  left: calc(var(--ribbon-left) - 1.7%);
  width: 40%;
  height: 3px;
  background: #111;
  opacity: 0.9;
  border-radius: 2px;

  transform: rotate(var(--rot));
  transform-origin: left center;
`;

const ResponsiveFrame = styled(Frame)`
  @media (max-width: 768px) {
    --logo-w: 42%;
    --logo-top: 16%;
    --ribbon-top: 31%;
    --ribbon-left: 7%;
    --ribbon-right: 7%;
    --ribbon-pad-v: 15%;
    --title-offset-v: 6.4%;
    --title-top: var(--title-top-mobile, 38%);
    --title-side-pad: 5.5%;
  }

  --title-top: var(--title-top-desktop, 40%);
`;

const BottomRightAccent = styled.div<{ $color: string }>`
  position: absolute;
  z-index: 5;

  right: var(--accent-right);
  bottom: var(--accent-bottom);

  width: clamp(4px, var(--accent-w), 12px);
  height: clamp(10px, var(--accent-h), 36px);

  background: ${({ $color }) => $color};
  opacity: 0.95;

  transform: rotate(var(--rot));
  transform-origin: right center;

  @media (max-width: 900px) {
    --accent-right: 25%;
    --accent-bottom: 13%;
    --accent-w: 1.3%;
    --accent-h: 4%;
  }

  @media (max-width: 768px) {
    --accent-right: 29.5%;
    --accent-bottom: 13.2%;
    --accent-w: 1.8%;
    --accent-h: 5.5%;
  }
`;

const stripTrailingYear = (name?: string) =>
  (name ?? '').replace(/\s*(?:19|20)\d{2}\s*$/u, '').trim();

export default function DocImageGenerator({
  templateSrc,
  docLabel,
  logoUrl,
  className,
}: DocImageGeneratorProps) {
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  const eventColor = sluggedEvent?.design?.themeColor || '#00A0E3';

  const cleanEventName = useMemo(
    () => stripTrailingYear(sluggedEvent?.name),
    [sluggedEvent?.name]
  );

  const effectiveLogo = useMemo(() => {
    if (logoUrl) return logoUrl;
    const d: any = sluggedEvent?.design ?? {};
    return (
      getImageSrc(d.eventLogoPosSvg) ||
      getImageSrc(d.eventLogoNegSvg) ||
      getImageSrc(d.logoPosImagePng) ||
      getImageSrc(d.logoNegImagePng) ||
      ''
    );
  }, [logoUrl, sluggedEvent]);

  const isLongTitle = (cleanEventName || '').length > 38;

  return (
    <ResponsiveFrame
      className={className}
      {...(isLongTitle && {
        style: {
          ['--title-top-desktop' as any]: '35%',
          ['--title-top-mobile' as any]: '34%',
        } as React.CSSProperties,
      })}
    >
      <Wrap>
        <Bg src={templateSrc} alt="" />
        {!!effectiveLogo && <Logo src={effectiveLogo} alt="event logo" />}

        <Title>{cleanEventName}</Title>

        <BottomRibbon $color={eventColor}>{docLabel}</BottomRibbon>
        <RibbonTopCap />
        <BottomRightAccent $color={eventColor} />
      </Wrap>
    </ResponsiveFrame>
  );
}
